import {useCallback, useEffect, useState} from 'react';

import {useAnnotatableContentPresence, useAnnotationModeStore} from './AnnotationModeStore';
import {buildAnchor, isMarkableRange} from './annotationAnchors';
import {computeAnnotationMerge, findContainingAnnotation} from './annotationMerge';
import {
  clearRegionHighlights,
  hasRegionHighlights,
  setRegionHighlights
} from './annotationPaintCoordinator';
import type {TextProjection} from './annotationTextProjection';
import type {
  Annotation,
  AnnotationAnchor,
  AnnotationMerge,
  AnnotationRange
} from './annotationTypes';
import {
  activeSelectionRange,
  annotationAnchorAtPoint,
  findAnnotationAtPoint,
  getSelectionNormalizedRange,
  hasUntypesetMath,
  isHighlightApiSupported,
  resolveAnnotationRanges,
  selectionCoversTextOutside
} from './annotationUtils';
import {useTextProjection} from './useTextProjection';

const EMPTY_ANNOTATIONS: Annotation[] = [];

/**
 * `containerRef` is a callback ref so the effects re-run if the element
 * attaches late (e.g. the feature flag resolving after first render).
 */
export function useAnnotations(
  regionId: string,
  lang: string | null = null
): {
  captureAnnotation: () => AnnotationAnchor | null;
  getAnnotationAtPoint: (x: number, y: number) => AnnotationAnchor | null;
  containerRef: (element: HTMLElement | null) => void;
  /** The region element once it has attached; null before that. */
  container: HTMLElement | null;
  getProjection: () => TextProjection | null;
  isMergeConfirmPending: boolean;
  /** Applies the pending merge; returns the merged mark so its popover can open. */
  confirmPendingMerge: () => AnnotationAnchor | null;
  cancelPendingMerge: () => void;
} {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [pendingMerge, setPendingMerge] = useState<{merge: AnnotationMerge; rect: DOMRect} | null>(
    null
  );
  const annotations = useAnnotationModeStore(
    (state) => state.annotationsByRegionId[regionId] ?? EMPTY_ANNOTATIONS
  );
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const addAnnotation = useAnnotationModeStore((state) => state.addAnnotation);
  const mergeAnnotations = useAnnotationModeStore((state) => state.mergeAnnotations);
  useAnnotatableContentPresence(Boolean(container?.textContent?.trim()));

  const getProjection = useTextProjection(container, {
    onMutate: () => clearRegionHighlights(regionId),
    // Deferred closure: `repaint` is declared below; the hook reads handlers
    // through a ref, so the latest one always fires.
    onSettle: () => repaint()
  });

  const repaint = useCallback(() => {
    if (!container || !isHighlightApiSupported()) {
      return;
    }
    const projection = annotations.length > 0 ? getProjection() : null;
    setRegionHighlights(
      regionId,
      projection ? resolveAnnotationRanges(projection, annotations) : new Map()
    );
  }, [container, regionId, annotations, getProjection]);

  /** The mark this selection lands on, applying the #19010 overlap policy. */
  const addOrMergeAnnotation = useCallback(
    (projection: TextProjection, range: AnnotationRange, rect: DOMRect): string | null => {
      // Each gesture answers for itself; a question left over from an earlier one
      // must never outlive it.
      setPendingMerge(null);
      const containing = findContainingAnnotation(projection, annotations, range, lang);
      if (containing) return containing.id;

      const merge = computeAnnotationMerge(projection, annotations, range, lang);
      if (merge) {
        if (merge.absorbedIds.length > 0) {
          setPendingMerge({merge, rect});
          return null;
        }
        mergeAnnotations(merge);
        return merge.intoId;
      }

      const anchor = buildAnchor(projection, range, {regionId, lang});
      return anchor && addAnnotation(anchor);
    },
    [annotations, lang, regionId, addAnnotation, mergeAnnotations]
  );

  const captureAnnotation = useCallback((): AnnotationAnchor | null => {
    // Selection first, so a plain click never pays for a projection build.
    const selection = activeSelectionRange();
    if (!selection || !container) return null;
    // A cross-region drag is rejected outright, never clamped to the part one
    // region happens to cover.
    if (selectionCoversTextOutside(selection, container)) return null;
    const projection = getProjection();
    const range = projection && getSelectionNormalizedRange(projection);
    if (!projection || !range || !isMarkableRange(projection, range)) return null;

    // The popover anchors to the gesture: a merged mark can extend far past
    // where the student released, and the popover hangs below its anchor.
    const rect = selection.getBoundingClientRect();
    const id = addOrMergeAnnotation(projection, range, rect);
    if (!id) return null;
    // Consume the selection only when the gesture landed, so a failed capture
    // stays selectable for copying.
    window.getSelection()?.removeAllRanges();
    return {rect, id};
  }, [container, getProjection, addOrMergeAnnotation]);

  const confirmPendingMerge = useCallback((): AnnotationAnchor | null => {
    if (!pendingMerge) {
      return null;
    }
    mergeAnnotations(pendingMerge.merge);
    window.getSelection()?.removeAllRanges();
    setPendingMerge(null);
    return {rect: pendingMerge.rect, id: pendingMerge.merge.intoId};
  }, [pendingMerge, mergeAnnotations]);

  const cancelPendingMerge = useCallback(() => setPendingMerge(null), []);

  const getAnnotationAtPoint = useCallback(
    (x: number, y: number): AnnotationAnchor | null => {
      const projection = annotations.length > 0 ? getProjection() : null;
      if (!container || !projection) {
        return null;
      }
      const id = findAnnotationAtPoint(container, projection, x, y, annotations);
      return id ? annotationAnchorAtPoint(x, y, id) : null;
    },
    [container, getProjection, annotations]
  );

  useEffect(() => {
    // With nothing painted yet, MathJax rewrites are waited out (no flash
    // against stale DOM); once painted, annotation changes always resync.
    if (container && (hasRegionHighlights(regionId) || !hasUntypesetMath(container))) {
      repaint();
    }
  }, [container, regionId, repaint]);

  // The region's paint dies with its container (unmount, region switch, detach).
  useEffect(() => {
    if (!container) {
      return undefined;
    }
    return () => clearRegionHighlights(regionId);
  }, [container, regionId]);

  return {
    captureAnnotation,
    getAnnotationAtPoint,
    containerRef: setContainer,
    container,
    getProjection,
    isMergeConfirmPending: isAnnotationModeOn && pendingMerge !== null,
    confirmPendingMerge,
    cancelPendingMerge
  };
}
